import { EventTag } from '@mortgage-pos/types'

type NewRelicMessageAndTag = {
  message: string
  tags?: EventTag[]
}

// error messages implicitly surfaced from the client
enum ImplicitErrorKeys {
  NetworkError = 'Network Error',
  RequestAborted = 'Request aborted',
  TimeoutExceeded = 'timeout exceeded',
}

// map New Relic messages to ignored rollbar errors
const newRelicRollbarErrorMap: Record<
  ImplicitErrorKeys,
  NewRelicMessageAndTag
> = {
  [ImplicitErrorKeys.NetworkError]: {
    message: 'network_error',
    tags: [{ key: 'type', value: 'general' }],
  },
  [ImplicitErrorKeys.RequestAborted]: {
    message: 'network_error',
    tags: [{ key: 'type', value: 'request_aborted' }],
  },
  [ImplicitErrorKeys.TimeoutExceeded]: {
    message: 'network_error',
    tags: [{ key: 'type', value: 'timeout_exceeded' }],
  },
}

interface NewRelicParam {
  increment: (arg0: string, arg1: any) => {}
}

const BROWSER_EXTENSION_PATTERNS = [
  'runtime.sendMessage()',
  'chrome-extension://',
  'safari-web-extension://',
  'moz-extension://',
]

const ONETRUST_MESSAGE_PATTERNS = ['.DomainData', ".Domain'"]

const ONETRUST_FRAME_PATTERN = 'cdn.cookielaw.org'

function getRollbarPayloadDetails(payload: Record<string, any>) {
  const message =
    payload?.body?.trace?.exception?.message ??
    payload?.body?.trace?.exception?.description ??
    ''
  const frames: any[] = payload?.body?.trace?.frames ?? []
  return { message, frames }
}

export function isBrowserExtensionError(payload: Record<string, any>): boolean {
  const { message, frames } = getRollbarPayloadDetails(payload)

  if (BROWSER_EXTENSION_PATTERNS.some((p) => message.includes(p))) {
    return true
  }

  return frames.some((frame) =>
    BROWSER_EXTENSION_PATTERNS.some((p) => frame?.filename?.includes(p))
  )
}

export function isThirdPartyScriptError(payload: Record<string, any>): boolean {
  const { message, frames } = getRollbarPayloadDetails(payload)

  if (ONETRUST_MESSAGE_PATTERNS.some((p) => message.includes(p))) {
    return true
  }

  return frames.some((frame: any) =>
    frame?.filename?.includes(ONETRUST_FRAME_PATTERN)
  )
}

// Payload type: https://explorer.docs.rollbar.com/#operation/create-item
export const aggregateNewRelicErrors = (
  newRelic: NewRelicParam,
  payload: Record<string, any>
): boolean => {
  const message =
    payload?.trace?.exception?.message ??
    payload?.trace?.exception?.description ??
    payload?.body?.trace?.exception?.message ??
    payload?.body?.trace?.exception?.description

  if (!message) return false

  const messageTrimmed = message?.trim()

  if (Object.keys(newRelicRollbarErrorMap).includes(messageTrimmed)) {
    newRelic.increment(
      newRelicRollbarErrorMap[messageTrimmed].message,
      newRelicRollbarErrorMap[messageTrimmed].tags
    )

    return true
  }
  return false
}
